import { MeshReflectorMaterial } from '@react-three/drei'
import { useAppStore } from '@store'
import { Item, Material, Textures, Zone } from '@types'
import { toVector3 } from '@utils/conversion/toVector3'
import { handleNodeMorphs, handleNodeTextures } from '@utils/sceneObjects/getZonesFromGLB'
import {
  hasDiffuseTextureMap,
  resolveApiTextureColor,
  resolveZoneMaterialProps,
} from '@utils/sceneObjects/resolvePbrMaterial'
import {
  resolveFabricEnvMapIntensity,
  resolveFabricRoughness,
} from '@utils/sceneObjects/fabricTextureQuality'
import { isConservativeFabricTexture } from '@utils/sceneObjects/resolveTextureTiling'
import { isContiguousSurfaceZone } from '@utils/sceneObjects/isContiguousSurfaceZone'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { DoubleSide, FrontSide, Mesh, Texture } from 'three'

// Interfaz para los elementos del grupo contiguo
interface ContiguousFurnitureItem {
  id: string
  catalogID: string
  position: { x: number; y: number; z: number }
  width: number
  height: number
  thickness: number
  rotation: number
  orderIndex: number
  type: string
  description: string
}
// Interfaz para el resultado del análisis de muebles contiguos
interface ContiguousAnalysisResult {
  groups: ContiguousFurnitureItem[][]
  totalGroups: number
  totalItemsInGroups: number
}

type ObjectMeshProps = {
  node: Mesh & { name: string }
  zones: Zone
  morphs: Item['morphs']
  textures: Textures
  renderTarget?: Texture
  paginaPdf?: number | undefined
  itemHeight?: number
  itemWidth?: number
  itemThickness?: number
  getContiguousItems?: () => ContiguousAnalysisResult
  fatherId?: string
  universal?: boolean
  itemId?: string
}

// Función de comparación personalizada para evitar re-renders innecesarios durante hover
// Comparar el contenido de textures y zones en lugar de solo la referencia para detectar cambios
const arePropsEqual = (prevProps: ObjectMeshProps, nextProps: ObjectMeshProps) => {
  // Comparar textures por contenido (URLs) en lugar de por referencia
  const texturesEqual =
    prevProps.textures.length === nextProps.textures.length &&
    prevProps.textures.every((prevTex, index) => {
      const nextTex = nextProps.textures[index]
      return (
        prevTex.zoneName === nextTex.zoneName &&
        prevTex.url === nextTex.url &&
        prevTex.searchID === nextTex.searchID
      )
    })

  // Comparar zones por contenido del material, no solo por referencia
  // Esto es crítico para detectar cuando se aplica herencia y el material cambia
  const zonesEqual = (() => {
    if (prevProps.zones === nextProps.zones) return true

    // Si las referencias son diferentes, comparar el contenido del material para la zona actual
    const prevZoneKeys = Object.keys(prevProps.zones || {})
    const nextZoneKeys = Object.keys(nextProps.zones || {})

    if (prevZoneKeys.length !== nextZoneKeys.length) return false

    return prevZoneKeys.every((zoneKey) => {
      const prevZone = prevProps.zones[zoneKey]
      const nextZone = nextProps.zones[zoneKey]

      if (!prevZone || !nextZone) return prevZone === nextZone

      // Comparar propiedades críticas del material
      const prevMaterial = prevZone.material
      const nextMaterial = nextZone.material

      if (!prevMaterial || !nextMaterial) return prevMaterial === nextMaterial

      return (
        prevMaterial.url === nextMaterial.url &&
        prevMaterial.searchID === nextMaterial.searchID &&
        prevMaterial.roughness === nextMaterial.roughness &&
        prevMaterial.metalness === nextMaterial.metalness &&
        prevMaterial.envMapIntensity === nextMaterial.envMapIntensity &&
        prevMaterial.opacity === nextMaterial.opacity &&
        prevMaterial.color === nextMaterial.color
      )
    })
  })()

  return (
    prevProps.node === nextProps.node &&
    zonesEqual &&
    texturesEqual &&
    prevProps.morphs === nextProps.morphs &&
    prevProps.renderTarget === nextProps.renderTarget &&
    prevProps.paginaPdf === nextProps.paginaPdf &&
    prevProps.itemHeight === nextProps.itemHeight &&
    prevProps.itemWidth === nextProps.itemWidth &&
    prevProps.itemThickness === nextProps.itemThickness &&
    prevProps.getContiguousItems === nextProps.getContiguousItems &&
    prevProps.fatherId === nextProps.fatherId &&
    prevProps.universal === nextProps.universal &&
    prevProps.itemId === nextProps.itemId
  )
}

export const ObjectMesh = React.memo(function ObjectMesh({
  node,
  zones,
  morphs,
  textures,
  renderTarget,
  paginaPdf,
  itemHeight = 0,
  itemWidth = 0,
  itemThickness = 0,
  getContiguousItems,
  fatherId,
  universal,
  itemId,
}: ObjectMeshProps) {
  const { name, uuid, material, ...rest } = node
  const isModalOpen = useAppStore.use.isModalOpen()
  const zoneValues = zones?.[name]?.material as Material
  const resolvedItemId = itemId ?? fatherId
  const zoneTexture = textures.find((texture) => texture.zoneName === name)
  const isFabric = zoneTexture ? isConservativeFabricTexture(zoneTexture, resolvedItemId) : false

  const repeatX = zoneValues?.repeatX
  const repeatY = zoneValues?.repeatY
  const textureRotation = zoneValues?.textureRotation
  const contadorModificaciones = useAppStore((s) => s.contadorModificaciones)
  const watchGlobalContador = isContiguousSurfaceZone(textures, name)
  const nonUniversalMaterialKey = zoneValues
    ? `${zoneValues.url}-${zoneValues.color}-${zoneValues.roughness}-${zoneValues.metalness}-${zoneValues.envMapIntensity}-${zoneValues.opacity}`
    : ''

  // Para objetos universales (como holes), handleNodeTextures modifica directamente node.material.map
  // de forma asíncrona, por lo que debemos usar directamente la textura del material
  const textureInitializedRef = useRef(false)
  const materialTextureRef = useRef<Texture | null>(null)
  const lastTextureUrlRef = useRef<string | null>(null)
  // Inicializar lastContadorRef con el valor actual para evitar que siempre sea 0
  const lastContadorRef = useRef<number>(contadorModificaciones)
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastTexturesRef = useRef<string>('')
  // Ref para detectar cambios en las propiedades del material de la zona
  const lastZoneMaterialKeyRef = useRef<string>('')
  // Usar estado en lugar de ref para forzar re-render cuando cambia la textura
  const [stableTexture, setStableTexture] = useState<Texture | undefined>(undefined)
  const [nonUniversalTexture, setNonUniversalTexture] = useState<Texture | undefined>(undefined)

  const appliedMap = universal
    ? (stableTexture ?? node.material?.map)
    : (nonUniversalTexture ?? node.material?.map)
  const hasTextureMap = hasDiffuseTextureMap(zoneValues, {
    zoneName: name,
    textures,
    appliedMap,
  })
  const pbr = resolveZoneMaterialProps(zoneValues, hasTextureMap, {
    zoneName: name,
    textures,
    appliedMap,
  })
  const color = pbr.color
  const roughness = isFabric ? resolveFabricRoughness(pbr.roughness) : pbr.roughness
  const metalness = pbr.metalness
  const envMapIntensity = isFabric
    ? resolveFabricEnvMapIntensity(pbr.envMapIntensity)
    : pbr.envMapIntensity
  const opacity = pbr.opacity

  // Aplicar propiedades visuales del material de forma SÍNCRONA antes del render
  useLayoutEffect(() => {
    if (universal) {
      // Obtener valores del material desde zoneValues o directamente del store
      let materialValues = zoneValues
      if (!materialValues && itemId) {
        const store = useAppStore.getState()
        const currentItem = store.items.get(itemId) || store.areas.get(itemId)
        materialValues = currentItem?.zones?.[name]?.material
      }

      if (!materialValues) return

      // Solo aplicar propiedades visuales que NO causan flash blanco
      const roughness = materialValues.roughness != null ? materialValues.roughness : 1
      const metalness = materialValues.metalness != null ? materialValues.metalness : 0
      const envMapIntensity =
        materialValues.envMapIntensity != null ? materialValues.envMapIntensity : 0
      const opacity = materialValues.opacity != null ? materialValues.opacity : 1

      const currentRoughness = node.material.roughness
      const currentMetalness = node.material.metalness
      const currentEnvMapIntensity = node.material.envMapIntensity
      const currentOpacity = node.material.opacity

      const needsUpdate =
        Math.abs(currentRoughness - roughness) > 0.001 ||
        Math.abs(currentMetalness - metalness) > 0.001 ||
        Math.abs(currentEnvMapIntensity - envMapIntensity) > 0.001 ||
        Math.abs(currentOpacity - opacity) > 0.001

      if (needsUpdate) {
        node.material.roughness = roughness
        node.material.metalness = metalness
        node.material.envMapIntensity = envMapIntensity
        node.material.opacity = opacity
        node.material.transparent = opacity < 1
        node.material.depthWrite = opacity >= 1
        node.material.needsUpdate = true
      }
      return
    }

    const zoneMaterial = zones?.[name]?.material
    const resolvedColor = resolveApiTextureColor(
      zoneMaterial?.color,
      hasDiffuseTextureMap(zoneMaterial, {
        zoneName: name,
        textures,
        appliedMap: node.material?.map,
      }),
    )
    if (typeof node.material?.color?.set === 'function') {
      node.material.color.set(resolvedColor)
    }
    node.material.needsUpdate = true
  }, [universal, zoneValues, node.material, name, itemId, zones, textures])

  useEffect(() => {
    if (!universal) return

    // Crear una clave única basada en las texturas para evitar llamadas innecesarias
    const texturesKey = textures.map((t) => `${t.zoneName}:${t.url}`).join('|')

    // Crear una clave única basada en las propiedades del material de la zona actual
    // Esto detecta cuando el material cambia (por ejemplo, cuando se aplica herencia)
    const zoneMaterialKey = zoneValues
      ? `${zoneValues.url}-${zoneValues.roughness}-${zoneValues.metalness}-${zoneValues.envMapIntensity}`
      : ''
    const zoneMaterialChanged = zoneMaterialKey !== lastZoneMaterialKeyRef.current

    // Verificar si realmente hay un cambio que procesar
    // Comparar tanto texturesKey como contadorModificaciones y zoneMaterialKey
    const texturesChanged = texturesKey !== lastTexturesRef.current
    const contadorChanged = contadorModificaciones !== lastContadorRef.current

    // Si no hay cambios, saltar
    if (!texturesChanged && !contadorChanged && !zoneMaterialChanged) {
      return
    }

    // Actualizar los refs SOLO cuando realmente hay cambios
    // Esto evita bucles infinitos pero permite que se ejecute cuando hay cambios reales
    if (texturesChanged) {
      lastTexturesRef.current = texturesKey
    }
    if (contadorChanged) {
      lastContadorRef.current = contadorModificaciones
    }
    if (zoneMaterialChanged) {
      lastZoneMaterialKeyRef.current = zoneMaterialKey
    }

    // const textureForZone = textures.find((t) => t.zoneName === name)
    // Verificar qué textura está realmente en el store para esta zona
    // const store = useAppStore.getState()
    // const currentObjectID = store.currentObjectID
    // const currentItem = currentObjectID
    //   ? store.items.get(currentObjectID) || store.areas.get(currentObjectID)
    //   : null
    // const storeTextureForZone = currentItem?.textures?.find((t: any) => t.zoneName === name)

    handleNodeTextures(
      node,
      zones,
      textures,
      repeatX,
      repeatY,
      textureRotation,
      paginaPdf,
      itemHeight,
      itemWidth,
      itemThickness,
      getContiguousItems,
      fatherId,
      universal,
      itemId,
    )
    // Inicializar el ref con la textura actual del material si existe
    if (node.material?.map && !materialTextureRef.current) {
      materialTextureRef.current = node.material.map
      lastTextureUrlRef.current =
        node.material.map.userData?.currentTexture || node.material.map.image?.src || null
    }
    if (!textureInitializedRef.current) {
      textureInitializedRef.current = true
    }
  }, [
    universal,
    node,
    zones,
    textures,
    repeatX,
    repeatY,
    textureRotation,
    paginaPdf,
    itemHeight,
    itemWidth,
    itemThickness,
    getContiguousItems,
    fatherId,
    itemId,
    contadorModificaciones, // Forzar actualización cuando cambia el material
  ])

  // Para objetos no universales, usar el método normal
  useEffect(() => {
    if (universal) return

    const currentMap = node.material?.map
    if (currentMap) {
      materialTextureRef.current = currentMap
    }
  }, [universal, node.material?.map])

  // Para objetos universales, sincronizar el estado con el material cuando cambia
  // Esto se ejecuta después de que handleNodeTextures aplica la textura asíncronamente
  useEffect(() => {
    if (!universal) return

    // Usar múltiples delays para asegurar que la textura se haya cargado
    // Primero intentar después de 100ms, luego después de 300ms si aún no hay textura
    let timeoutId1: NodeJS.Timeout
    let timeoutId2: NodeJS.Timeout

    const updateTexture = () => {
      const currentMap = node.material?.map
      if (currentMap) {
        const currentUrl = currentMap?.userData?.currentTexture || currentMap?.image?.src
        // Actualizar el estado cuando la textura cambia para forzar re-render
        if (currentUrl && currentUrl !== lastTextureUrlRef.current) {
          materialTextureRef.current = currentMap
          lastTextureUrlRef.current = currentUrl
          setStableTexture(currentMap) // Usar setState para forzar re-render
        } else if (!materialTextureRef.current && currentUrl) {
          // Inicializar si no hay ref pero hay textura
          materialTextureRef.current = currentMap
          lastTextureUrlRef.current = currentUrl
          setStableTexture(currentMap) // Usar setState para forzar re-render
        }
      }
    }

    // Primer intento después de 100ms
    timeoutId1 = setTimeout(updateTexture, 100)
    // Segundo intento después de 300ms por si la carga es más lenta
    timeoutId2 = setTimeout(updateTexture, 300)

    return () => {
      clearTimeout(timeoutId1)
      clearTimeout(timeoutId2)
    }
  }, [universal, contadorModificaciones, node.material?.version, name])

  // Para objetos no universales, ejecutar la carga/aplicación de textura como efecto
  // (handleNodeTextures tiene side-effects asíncronos; useMemo aquí provoca carreras).
  useEffect(() => {
    if (universal) return
    handleNodeTextures(
      node,
      zones,
      textures,
      repeatX,
      repeatY,
      textureRotation,
      paginaPdf,
      itemHeight,
      itemWidth,
      itemThickness,
      getContiguousItems,
      fatherId,
      universal,
      itemId,
    )

    const syncTexture = () => {
      const currentMap = node.material?.map
      if (currentMap) {
        setNonUniversalTexture(currentMap)
      }
    }

    syncTexture()
    const timeoutId = setTimeout(syncTexture, 60)
    return () => clearTimeout(timeoutId)
  }, [
    universal,
    node,
    zones,
    textures,
    repeatX,
    repeatY,
    textureRotation,
    paginaPdf,
    itemHeight,
    itemWidth,
    itemThickness,
    getContiguousItems,
    fatherId,
    itemId,
    ...(watchGlobalContador ? [contadorModificaciones] : []),
    nonUniversalMaterialKey,
  ])

  const newTexture: Texture | undefined = universal ? stableTexture : nonUniversalTexture

  useMemo(() => {
    handleNodeMorphs(node, morphs)
  }, [morphs, node])
  const width = useMemo(() => {
    const prewidht = morphs.find((el) => el.name === 'Ancho')
    return prewidht
  }, [morphs])

  // const edgeGeometry = useMemo(() => new THREE.EdgesGeometry(node.geometry), [node.geometry])
  // const shouldShowLines = useMemo(() => mode === 'MODE_IDLE' && prefs.LINEASENPLANTA, [mode, prefs])

  if (zoneValues?.mirror) {
    return (
      <mesh
        {...rest}
        castShadow={opacity < 1 ? false : true}
        receiveShadow
        scale={toVector3([
          width?.min !== width?.max ? (width?.default ?? 1) / (width?.min ?? 1) : 1,
          1,
          1,
        ])}
      >
        <MeshReflectorMaterial
          key={`${zoneValues.url}-mesh-${uuid}-${zoneValues}`}
          mirror={1}
          resolution={1024}
          color={color}
          opacity={opacity}
          blur={[0, 0]}
          side={FrontSide}
          map={newTexture || materialTextureRef.current}
          roughness={roughness}
          metalness={isModalOpen ? 1 : 0}
          envMap={renderTarget}
          depthWrite={opacity < 1 ? false : true}
          transparent={opacity < 1 ? true : false}
        />
        {/* {shouldShowLines && (
          <lineSegments geometry={edgeGeometry}>
            <lineBasicMaterial
              color='black'
              linewidth={2}
            />
          </lineSegments>
        )} */}
      </mesh>
    )
  }

  // Para objetos universales, las propiedades visuales se aplican directamente en handleNodeTextures
  // NO usar props de React Three Fiber para estas propiedades porque las sobrescribirían con valores por defecto
  const meshProps = universal
    ? {
        'material-envMap': envMapIntensity > 0 ? renderTarget : undefined,
        'material-depthWrite': opacity < 1 ? false : true,
        'material-transparent': opacity < 1 ? true : false,
      }
    : {
        'material-roughness': roughness,
        'material-metalness': metalness,
        'material-envMapIntensity': envMapIntensity,
        'material-envMap': envMapIntensity > 0 ? renderTarget : undefined,
        'material-depthWrite': opacity < 1 ? false : true,
        'material-transparent': opacity < 1 ? true : false,
      }

  return (
    <mesh
      key={`${zoneValues?.url}-mesh-${uuid}`}
      material={material}
      {...rest}
      castShadow={opacity < 1 ? false : true}
      receiveShadow
      material-color={color}
      material-opacity={opacity}
      material-side={DoubleSide}
      material-map={universal ? stableTexture : newTexture}
      {...meshProps}
    >
      {/* {shouldShowLines && (
        <lineSegments geometry={edgeGeometry}>
          <lineBasicMaterial
            color='black'
            linewidth={2}
          />
        </lineSegments>
      )} */}
    </mesh>
  )
}, arePropsEqual)

// Ojo NO BORRAR hay que revisar la descomentada porque getContiguousItems esta fastidiando
// OJO PORQUE ES LO NORMAL si no añades getContiguousItems a handleNodeTextures nunca va a funcionar
// revisa el handleNodeTextures de arriba y el de aqui abajo y se ve la diferencia claramente

// import { MeshReflectorMaterial } from '@react-three/drei'
// import { useAppStore } from '@store'
// import { Item, Material, Textures, Zone } from '@types'
// import { toVector3 } from '@utils/conversion/toVector3'
// import { handleNodeMorphs, handleNodeTextures } from '@utils/sceneObjects/getZonesFromGLB'
// import { useMemo } from 'react'
// import { DoubleSide, FrontSide, Mesh, Texture } from 'three'

// type ObjectMeshProps = {
//   node: Mesh & { name: string }
//   zones: Zone
//   morphs: Item['morphs']
//   textures: Textures
//   renderTarget?: Texture
//   paginaPdf: number | undefined
//   itemHeight: number
//   itemWidth: number
// }

// export function ObjectMesh({
//   node,
//   zones,
//   morphs,
//   textures,
//   renderTarget,
//   paginaPdf,
//   itemHeight,
//   itemWidth,
// }: ObjectMeshProps) {
//   const mode = useAppStore.use.mode()
//   const prefs = useAppStore.use.prefs()
//   const { name, uuid, children, userData, material, ...rest } = node
//   const isModalOpen = useAppStore.use.isModalOpen()
//   const zoneValues = zones?.[name]?.material as Material
//   // if (!zoneValues) return null
//   // const {
//   //   color,
//   //   roughness,
//   //   metalness,
//   //   envMapIntensity,
//   //   opacity,
//   //   repeatY,
//   //   repeatX,
//   //   textureRotation,
//   // } = zoneValues
//   const color = zoneValues?.color
//   const roughness = zoneValues?.roughness
//   const metalness = zoneValues?.metalness
//   const envMapIntensity = zoneValues?.envMapIntensity
//   const opacity = zoneValues?.opacity
//   const repeatX = zoneValues?.repeatX
//   const repeatY = zoneValues?.repeatY
//   const textureRotation = zoneValues?.textureRotation

//   const newTexture = useMemo(
//     () =>
//       handleNodeTextures(
//         node,
//         zones,
//         textures,
//         repeatX,
//         repeatY,
//         textureRotation,
//         paginaPdf,
//         itemHeight,
//         itemWidth,
//       ),
//     [node, zones, textures, repeatX, repeatY, textureRotation, paginaPdf, itemHeight, itemWidth],
//   )
//   useMemo(() => {
//     handleNodeMorphs(node, morphs)
//   }, [...morphs, renderTarget?.uuid])
//   const width = useMemo(() => {
//     const prewidht = morphs.find((el) => el.name === 'Ancho')
//     return prewidht
//   }, [morphs])
//   // const edgeGeometry = useMemo(() => new THREE.EdgesGeometry(node.geometry), [node.geometry])
//   // const shouldShowLines = useMemo(() => mode === 'MODE_IDLE' && prefs.LINEASENPLANTA, [mode, prefs])
//   if (zoneValues?.mirror) {
//     return (
//       <mesh
//         {...rest}
//         castShadow={opacity < 1 ? false : true}
//         receiveShadow
//         scale={toVector3([
//           width?.min !== width?.max ? (width?.default ?? 1) / (width?.min ?? 1) : 1,
//           1,
//           1,
//         ])}
//       >
//         <MeshReflectorMaterial
//           key={`${zoneValues.url}-mesh-${uuid}-${zoneValues}`}
//           mirror={1}
//           resolution={1024}
//           color={color}
//           opacity={opacity}
//           blur={[0, 0]}
//           side={FrontSide}
//           map={newTexture}
//           roughness={roughness}
//           metalness={isModalOpen ? 1 : 0}
//           envMap={renderTarget}
//           depthWrite={opacity < 1 ? false : true}
//           transparent={opacity < 1 ? true : false}
//         />
//         {/* {shouldShowLines && (
//           <lineSegments geometry={edgeGeometry}>
//             <lineBasicMaterial
//               color='black'
//               linewidth={2}
//             />
//           </lineSegments>
//         )} */}
//       </mesh>
//     )
//   }
//   return (
//     <mesh
//       key={`${zoneValues?.url}-mesh-${uuid}`}
//       material={material}
//       {...rest}
//       castShadow={opacity < 1 ? false : true}
//       receiveShadow
//       material-color={color}
//       material-opacity={opacity}
//       material-side={DoubleSide}
//       material-map={newTexture}
//       material-roughness={roughness}
//       material-metalness={metalness}
//       material-envMapIntensity={envMapIntensity}
//       material-envMap={renderTarget}
//       material-depthWrite={opacity < 1 ? false : true}
//       material-transparent={opacity < 1 ? true : false}
//     >
//       {/* {shouldShowLines && (
//         <lineSegments geometry={edgeGeometry}>
//           <lineBasicMaterial
//             color='black'
//             linewidth={2}
//           />
//         </lineSegments>
//       )} */}
//     </mesh>
//   )
// }
